import React, { useEffect, useMemo } from 'react';
import { scoreGroupTypeOptions } from './constants';
import {
  useHitScoreGroupType,
  useSelectedHitButton,
  useSelectedHitScoreGroups,
  useUvHitsOnly,
} from 'redux/hooks/header';
import {
  ESelectedHitButton,
  setHitButton,
  setHitScoreGroup,
  setHitScoreGroupType,
  setSelectedHitScoreGroups,
  setUvHitsOnly,
} from 'redux/reducers/headerReducer';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import { FmlxButton, FmlxCheckBox, FmlxDropdown } from 'fmlx-common-ui';
import { useAllScoresGroups } from 'redux/hooks/common';
import { HitType, ScoreGroupType } from '../Contants';
import RMMultiSelectSearchDropdown from 'components/customComponents/CommonComponents/RMMultiSelectSearchDropdown';
import { useAllPlates } from 'redux/hooks/experiment';
import { FilterWell } from '../utils';
import { computeAllHitWells, computeAnyHitWells } from './hitsFilterUtils';
import { getUsedScoreGroupIds } from '../filterComputations';
import './HitsFilter.scss';

const hitButtonToType = (hitButton: ESelectedHitButton): HitType | undefined => {
  if (hitButton === ESelectedHitButton.AllHits) {
    return HitType.All;
  }
  if (hitButton === ESelectedHitButton.AnyHits) {
    return HitType.Any;
  }
  return undefined;
};

const hitTypeToButton = (hitsType: HitType | undefined): ESelectedHitButton => {
  if (hitsType === HitType.All) {
    return ESelectedHitButton.AllHits;
  }
  if (hitsType === HitType.Any) {
    return ESelectedHitButton.AnyHits;
  }
  return ESelectedHitButton.None;
};

interface HitsFilterProps {
  usedScoreGroupIds?: Set<number>;
  onHighlightedWells?: (wells: FilterWell[]) => void;
}

const HitsFilter = ({ usedScoreGroupIds, onHighlightedWells }: HitsFilterProps) => {
  const scoreGroupType = useHitScoreGroupType();
  const uvHitsOnly = useUvHitsOnly();
  const hitButton = useSelectedHitButton();
  const selectedHitScoreGroups = useSelectedHitScoreGroups();
  const hitsType = hitButtonToType(hitButton);
  const allScoreGroups = useAllScoresGroups() ?? [];
  const dispatch = useRMDispatch();
  const plateDto = useAllPlates();
  const inspections = useRMSelector((state) => state?.experiment?.expData?.inspections);
  const inspectionId = useRMSelector(
    (state) =>
      state?.experiment?.wellSelectionDetails?.selectedDropImageParameters?.inspection
        ?.id,
  );
  const dropNumber = useRMSelector(
    (state) =>
      state?.experiment?.wellSelectionDetails?.selectedDropImageParameters?.dropNumber,
  );

  const scoreGroupTypeItems = useMemo(
    () =>
      scoreGroupTypeOptions.map((sgt) => ({
        group: '',
        id: sgt.value,
        text: sgt.name,
      })),
    [],
  );

  const plateScoreGroupIds = useMemo(() => getUsedScoreGroupIds(plateDto), [plateDto]);
  const activeScoreGroupIds = usedScoreGroupIds ?? plateScoreGroupIds;

  const scoreGroupItems = useMemo(() => {
    const canvasGroups = allScoreGroups.filter((sg) =>
      activeScoreGroupIds.has(sg.scoreGroupsId),
    );

    const filteredGroups =
      scoreGroupType === ScoreGroupType.Auto
        ? canvasGroups.filter((sg) => sg.isAutoScore)
        : scoreGroupType === ScoreGroupType.Manual
        ? canvasGroups.filter((sg) => !sg.isAutoScore)
        : canvasGroups;

    return filteredGroups.map((sg) => ({
      group: '',
      id: sg.scoreGroupsId,
      text: sg.name,
    }));
  }, [allScoreGroups, scoreGroupType, activeScoreGroupIds]);

  useEffect(() => {
    const allIds = scoreGroupItems.map((item) => item.id);
    if (selectedHitScoreGroups === null) {
      if (allIds.length === 0) {
        return;
      }
      dispatch(setSelectedHitScoreGroups(allIds));
      return;
    }

    const validIds = new Set(allIds);
    const next = selectedHitScoreGroups.filter((id) => validIds.has(id));
    if (
      next.length === selectedHitScoreGroups.length &&
      next.every((id, index) => id === selectedHitScoreGroups[index])
    ) {
      return;
    }
    dispatch(setSelectedHitScoreGroups(next));
  }, [dispatch, scoreGroupItems, selectedHitScoreGroups]);

  const hitWells = useMemo(() => {
    if (!onHighlightedWells) {
      return [];
    }
    if ((hitsType === undefined || hitsType === null) && !uvHitsOnly) {
      return [];
    }

    const compute = hitsType === HitType.All ? computeAllHitWells : computeAnyHitWells;

    return compute(
      inspections,
      plateDto,
      inspectionId,
      dropNumber,
      selectedHitScoreGroups ?? Array.from(plateScoreGroupIds),
      scoreGroupType as ScoreGroupType,
      uvHitsOnly,
    );
  }, [
    onHighlightedWells,
    hitsType,
    inspections,
    plateDto,
    inspectionId,
    dropNumber,
    selectedHitScoreGroups,
    plateScoreGroupIds,
    scoreGroupType,
    uvHitsOnly,
    onHighlightedWells,
  ]);

  useEffect(() => {
    onHighlightedWells?.(hitWells);
  }, [onHighlightedWells, hitWells]);

  const handleScoreGroupType = (id: number) => {
    const selectedScoreGroupType = scoreGroupTypeOptions.find(
      (option) => option.value === id,
    );
    dispatch(setHitScoreGroupType(selectedScoreGroupType?.name));
    dispatch(setHitScoreGroup(0));
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-row">
        <div className="w-[30%]">Score Type</div>
        <div className="w-[70%]">
          <FmlxDropdown
            id="finddrop-scoregroup-dropdown"
            size={FmlxDropdown.Size.SMALL}
            menuProps={{ width: '100%' } as any}
            menuPosition="default"
            items={scoreGroupTypeItems}
            selectedItem={
              scoreGroupTypeOptions.find((option) => option.name === scoreGroupType)
                ?.value ?? scoreGroupTypeOptions[0]?.value
            }
            onChange={handleScoreGroupType}
          />
        </div>
      </div>

      <div className="flex flex-row mt-2">
        <div className="w-[30%]">Group</div>
        <div className="w-[70%]">
          <RMMultiSelectSearchDropdown
            options={scoreGroupItems}
            getOptionId={(item) => item.id}
            getOptionLabel={(item) => item.text}
            onSelectionChange={(ids) => dispatch(setSelectedHitScoreGroups(ids))}
            selectedIds={selectedHitScoreGroups ?? []}
            enableLogic={false}
            enableSearch={false}
            enableSelectAll
            selectAllLabel="All Groups"
          />
        </div>
      </div>

      <div className="flex flex-row mt-2">
        <div className="w-[30%]">Hits</div>
        <div className="flex flex-row w-[70%] gap-x-2 finddrop-hits-filter">
          <FmlxButton
            id="finddrop-hits-all-button"
            onClick={() =>
              dispatch(
                setHitButton(
                  hitTypeToButton(hitsType === HitType.All ? undefined : HitType.All),
                ),
              )
            }
            size="sm"
            type="basic"
            variant="outline"
            selected={hitsType === HitType.All}
            label="ALL HITS"
          />
          <FmlxButton
            id="finddrop-hits-any-button"
            onClick={() =>
              dispatch(
                setHitButton(
                  hitTypeToButton(hitsType === HitType.Any ? undefined : HitType.Any),
                ),
              )
            }
            size="sm"
            type="basic"
            variant="outline"
            selected={hitsType === HitType.Any}
            label="ANY HITS"
          />
        </div>
      </div>

      <div className="flex flex-row mt-2">
        <div className="w-[30%]" />
        <div className="w-[70%]">
          <FmlxCheckBox
            checked={uvHitsOnly}
            label="UV Hits only"
            onChange={() => dispatch(setUvHitsOnly(!uvHitsOnly))}
          />
        </div>
      </div>
    </div>
  );
};

export default HitsFilter;
